<i18n src="@/locales/views/organization/certifications/query_formats/list_producers.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/producer.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/crop_field.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/crop_field_certification.yml" lang="yaml"></i18n>
<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('list_formats')"
    :params="{
     organizationId: organizationId,
     certificationId: certificationId,
     certificationVersionId: certificationVersionId
    }"
  ></go-back-link>
  <bread-crumbs :labels="[module_name, format_certification.certification?.name, format_certification.certification_version?.name, format_certification.name]" />

  <template v-if="load_data_general">
    <template v-if="producers.length > 0">
      <div class="mt-8 flex flex-col">
        <ag-grid-vue
          style="width: 100%; height: 650px"
          class="ag-theme-alpine w-full h-full"
          :columnDefs="columnDefs"
          @grid-ready="onGridReady"
          :defaultColDef="defaultColDef"
          :rowData="columnsData"
        >
        </ag-grid-vue>
      </div>

      <letter-paginator v-if="load_data_general" v-show="!filtered" v-model="current_page" />
    </template>
    <p v-else> {{ $t('labels.no_producers') }} </p>
  </template>

  <loading-message v-else />

</template>

<script>
import Requester from "@/views/organization/requesters/certifications/query_formats/list_producers.js";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";
import LetterPaginator from "@/components/views/layout/LetterPaginator.vue";

export default {
  name: "AECertificationsQueryFormatsListProducers",
  components: { AgGridVue, LetterPaginator },
  data() {
    return {
      load_data_general: false,
      format_certification: {},
      producers: [],
      current_page: "A",
      total_number_producers: 0,
      rows_per_page: 10,
      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: true,
      },
      columnDefs: [],
    };
  },
  props: {
    organizationId: { required: true },
    certificationId: { required: true },
    certificationVersionId: { required: true },
    formatCertificationId: { required: true }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organizationId,
        },
      }).then(() => this.loadData())
    );
  },
  computed: {
    columnsData() {
      let columns_data = [];
      for (let index = 0; index < this.producers.length; index++) {
        const producer = this.producers[index];
        const org_crop_fields = producer.organization_crop_fields;
        if (org_crop_fields.length == 0) {
          columns_data.push(this.parseProducer(producer));
        } else {
          org_crop_fields.forEach((org_crop_field) => {
            columns_data.push(this.parseProducer(producer, org_crop_field));
          });
        }
      }

      return columns_data;
    },
    can_filter() {
      return Object.values(this.filters).some(f => f !== "");
    }
  },
  watch: {
    current_page: function () {
      if (!this.filtered) {
        this.fetchProducers();
      }
    }
  },
  methods: {
    certificationLabel(field_certifications, certification_id) {
      let field_certification = field_certifications.find(
        (provider_cert) => provider_cert.certification_id == certification_id
      );
      if (field_certification) {
        let data = [];
        let days = 0;
        data[0] = days;
        if (field_certification.suspended) {
          data[1] = this.$t("model.crop_field_certification.suspended");
        }
        return data;
      }
      return [];
    },
    parseProducer: function (producer, org_crop_field = null) {
      return {
        id: producer.id,
        name: producer.name,
        lastname: producer.lastname,
        producer_code: producer.producer_code,
        organization_producer_code:
          producer.organization_producer.producer_code,
        crop_field: org_crop_field?.crop_field_code,
        route: this.routeName("show_producer"),
        route_params: {
          organization_id: this.organization_id,
          producer_id: producer.id,
        },
      };
    },
    loadData: async function () {
      await this.fetchFormatCertification();
      await this.fetchProducers();
      this.fillColumnsDef();
      this.load_data_general = true;
    },
    fetchFormatCertification: async function () {
      let response = await Requester.fetchFormatCertification({
        organization_id: this.organizationId,
        certification_id: this.certificationId,
        certification_version_id: this.certificationVersionId,
        format_certification_id: this.formatCertificationId
      });

      this.format_certification = response.data;
    },
    fetchProducers: async function () {
      let response = await Requester.fetchProducers({
        organization_id: this.organizationId,
        certification_id: this.certificationId,
        certification_version_id: this.certificationVersionId,
        format_certification_id: this.formatCertificationId,
        current_page: this.current_page
      });

      this.producers = response.data.producers;
    },
    downloadProducerData: async function(producerId, fullname) {
      try {
        // Here you would implement the actual download logic
        let response = await Requester.downloadFormat({
          organization_id: this.organizationId,
          certification_id: this.certificationId,
          certification_version_id: this.certificationVersionId,
          format_certification_id: this.formatCertificationId,
          producer_id: producerId
        });
        const file = response.data;

        this.downloadFile(file, `${this.format_certification.name} - ${fullname}.pdf`);
      } catch (error) {
        console.error("Error downloading producer data:", error);
      }
    },
    // Custom cell renderer for the download button
    downloadButtonRenderer(params) {
      const producerId = params.data.id;

      const button = document.createElement('button');
      button.className = 'btn-white';
      button.id = `download-button-${producerId}`;
      const downloadContentHTML = `
        <i class="fa-solid fa-file-excel"></i>
        <span class="space-icon"></span>
        ${this.$t("labels.download") || "Download"}
      `;

      button.innerHTML = downloadContentHTML;

      const downloadEvent = async () =>  {
        const btn = document.getElementById(`download-button-${producerId}`);
        btn.removeEventListener('click', downloadEvent);
        btn.innerHTML = `
          <i class="fa-solid fa-sync fa-spin"></i>
          <span class="space-icon"></span>
          ${this.$t("labels.downloading") || "Downloading"}
        `;
        await this.downloadProducerData(producerId, `${params.data.lastname} ${params.data.name}`);
        btn.innerHTML = downloadContentHTML;
        btn.addEventListener('click', downloadEvent);
      }

      button.addEventListener('click', downloadEvent);

      return button;
    },
    fillColumnsDef() {
      this.columnDefs = [
        {
          headerName: this.$t(
            "model.producer.organization_producer.producer_code"
          ),
          field: "organization_producer_code",
          filter: true,
          resizable: true,
          width: 350,
        },
        {
          headerName: this.$t("model.producer.producer_code"),
          field: "producer_code",
          filter: true,
          resizable: true,
          width: 350,
        },
        {
          headerName: this.$t("model.producer.lastname"),
          field: "lastname",
          filter: true,
          resizable: true,
          width: 350,
        },
        {
          headerName: this.$t("model.producer.name"),
          field: "name",
          filter: true,
          resizable: true,
          width: 350,
        },
        {
          headerName: this.$t("model.crop_field.self"),
          field: "crop_field",
          filter: true,
          resizable: true,
          width: 350,
        },
        {
          headerName: "",
          field: "download",
          sortable: false,
          filter: false,
          width: 150,
          cellRenderer: this.downloadButtonRenderer,
        },
      ];
    },
    sizeToFit() {
      this.gridApi.sizeColumnsToFit({
        defaultMinWidth: 250,
      });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.sizeToFit();
    },
  },
};
</script>
